import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Users, Radar, Satellite } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { useTheme } from './ThemeToggle';

const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function EventsCalendar({ customerId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const { isDarkMode } = useTheme();

  const loadEvents = async () => {
    try {
      const data = await api.getEvents();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const getEventsForDate = (date) => {
    return events.filter(e => {
      const eventDate = new Date(e.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return events
      .filter(e => {
        const eventDate = new Date(e.date);
        eventDate.setHours(23, 59, 59, 999);
        return eventDate >= now;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === tomorrow.toDateString()) return 'Demain';
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      const prevDate = new Date(year, month, -i);
      days.unshift({ date: prevDate, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <motion.div 
          className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Radar className="w-5 h-5 text-rose-400" />
        </motion.div>
      </div>
    );
  }

  const upcomingEvents = getUpcomingEvents();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #4c0519 100%)',
        boxShadow: '0 0 40px rgba(244, 63, 94, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(244, 63, 94, 0.3)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-rose-400"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3 + 0.1
            }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Radar sweep animation en fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
        <motion.div
          className="absolute top-1/2 left-1/2 w-[200%] h-1"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(244, 63, 94, 0.8) 50%, transparent 100%)',
            transformOrigin: 'left center'
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Bordure supérieure */}
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-rose-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-rose-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-rose-500/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-rose-500/30" />

      {/* Header */}
      <div className="relative p-4 flex items-center justify-between border-b border-rose-500/20">
        <div className="flex items-center gap-3">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.2) 0%, rgba(244, 63, 94, 0.1) 100%)',
              border: '1px solid rgba(244, 63, 94, 0.3)'
            }}
            animate={{ boxShadow: ['0 0 10px rgba(244, 63, 94, 0.2)', '0 0 20px rgba(244, 63, 94, 0.4)', '0 0 10px rgba(244, 63, 94, 0.2)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.span
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="text-xl"
            >
              📡
            </motion.span>
          </motion.div>
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              Scanner de Secteur
              {upcomingEvents.length > 0 && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-rose-500/20 text-rose-400 border border-rose-500/30">
                  {upcomingEvents.length} SIGNAL{upcomingEvents.length > 1 ? 'S' : ''}
                </span>
              )}
            </h3>
            <p className="text-[11px] text-rose-500/70 font-mono tracking-wider">
              DÉTECTION D'ACTIVITÉ • ZONE LOCALE
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-rose-500"
            animate={{ opacity: [1, 0.5, 1], scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <div className="flex gap-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded-lg text-xs font-mono transition-all ${
                viewMode === 'list' 
                  ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' 
                  : 'text-gray-500 hover:text-rose-400'
              }`}
            >
              LISTE
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 rounded-lg text-xs font-mono transition-all ${
                viewMode === 'calendar' 
                  ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' 
                  : 'text-gray-500 hover:text-rose-400'
              }`}
            >
              GRILLE
            </button>
          </div>
        </div>
      </div>

      <div className="relative p-4">
        {viewMode === 'list' ? (
          // Vue Liste
          <div className="space-y-3">
            {upcomingEvents.length === 0 ? (
              <div className="py-8 text-center">
                <motion.div
                  className="w-16 h-16 mx-auto mb-3 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center"
                  animate={{ scale: [1, 1.05, 1], boxShadow: ['0 0 10px rgba(244, 63, 94, 0.1)', '0 0 25px rgba(244, 63, 94, 0.3)', '0 0 10px rgba(244, 63, 94, 0.1)'] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  >
                    <Satellite className="w-8 h-8 text-rose-400/50" />
                  </motion.div>
                </motion.div>
                <p className="text-gray-400 font-medium">Aucune activité détectée</p>
                <p className="text-xs text-gray-600 mt-1 font-mono">SCAN EN COURS • Restez connecté !</p>
              </div>
            ) : (
              upcomingEvents.map((event, idx) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 rounded-xl border transition-all hover:border-rose-500/40"
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderColor: 'rgba(244, 63, 94, 0.2)'
                  }}
                >
                  <div className="flex items-start gap-3">
                    <motion.div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl border"
                      style={{ 
                        background: `${event.color || '#f43f5e'}15`,
                        borderColor: `${event.color || '#f43f5e'}30`
                      }}
                      animate={{ boxShadow: [`0 0 0px ${event.color || '#f43f5e'}00`, `0 0 15px ${event.color || '#f43f5e'}40`, `0 0 0px ${event.color || '#f43f5e'}00`] }}
                      transition={{ duration: 2, repeat: Infinity, delay: idx * 0.3 }}
                    >
                      {event.emoji || '🎉'}
                    </motion.div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{event.title}</h4>
                      <p className="text-sm mt-1 text-gray-400">{event.description}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs">
                        <span className="flex items-center gap-1 text-rose-400 font-mono">
                          <Clock className="w-3 h-3" />
                          {formatEventDate(event.date)}
                          {event.time && ` • ${event.time}`}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 text-gray-500">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </span>
                        )}
                        {event.participants > 0 && (
                          <span className="flex items-center gap-1 text-purple-400">
                            <Users className="w-3 h-3" />
                            {event.participants} participant(s)
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Signal indicator */}
                    <div className="flex flex-col items-center gap-1">
                      {[...Array(3)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="w-1 rounded-full bg-rose-500"
                          style={{ height: `${(i + 1) * 4}px` }}
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : (
          // Vue Calendrier (Grille)
          <div>
            {/* Navigation mois */}
            <div className="flex items-center justify-between mb-4 px-2">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 rounded-lg text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="font-mono text-white">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 rounded-lg text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Jours de la semaine */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(day => (
                <div key={day} className="text-center text-[10px] py-1 text-rose-500/50 font-mono">
                  {day}
                </div>
              ))}
            </div>

            {/* Grille du calendrier */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(currentDate).map((day, idx) => {
                const dayEvents = getEventsForDate(day.date);
                const isToday = day.date.toDateString() === new Date().toDateString();
                const isSelected = selectedDate?.toDateString() === day.date.toDateString();

                return (
                  <motion.button
                    key={idx}
                    onClick={() => setSelectedDate(day.date)}
                    className={`p-2 rounded-lg text-center transition-all relative ${
                      !day.isCurrentMonth ? 'text-gray-700' :
                      isSelected ? 'bg-rose-500 text-white' :
                      isToday ? 'bg-rose-500/30 text-rose-400 font-bold' :
                      'text-gray-300 hover:bg-rose-500/10'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-sm font-mono">{day.date.getDate()}</span>
                    {dayEvents.length > 0 && (
                      <div className="flex justify-center gap-0.5 mt-1">
                        {dayEvents.slice(0, 3).map((_, i) => (
                          <motion.div 
                            key={i} 
                            className="w-1 h-1 rounded-full bg-rose-400"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                          />
                        ))}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Événements du jour sélectionné */}
            <AnimatePresence>
              {selectedDate && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-rose-500/20"
                >
                  <p className="text-rose-400/70 text-xs mb-2 font-mono">
                    📅 {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase()}
                  </p>
                  {getEventsForDate(selectedDate).length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucun signal détecté</p>
                  ) : (
                    getEventsForDate(selectedDate).map(event => (
                      <motion.div 
                        key={event.id} 
                        className="p-3 rounded-xl text-sm border"
                        style={{
                          background: 'rgba(244, 63, 94, 0.1)',
                          borderColor: 'rgba(244, 63, 94, 0.2)'
                        }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        <span className="mr-2">{event.emoji || '🎉'}</span>
                        <span className="text-white">{event.title}</span>
                        {event.time && <span className="text-rose-400 ml-2 font-mono text-xs">• {event.time}</span>}
                      </motion.div>
                    ))
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-rose-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">📡 SECTOR SCANNER v2.0</span>
        <div className="flex items-center gap-1">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-rose-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
          <div className="w-1.5 h-1.5 rounded-full bg-rose-400/50" />
          <div className="w-1.5 h-1.5 rounded-full bg-rose-300/30" />
        </div>
      </div>
    </motion.div>
  );
}
